import React, {ReactNode, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {BAR_LAYER_ID} from '../../../components/Layout';
import {useToolbar} from '../../../components/ToolbarContext';
import {useContentWidth, ViewSwitchTab} from '../list-page/ListPageKit';
import '../list-page/ListPageKit.css';
import './RecordKit.css';

/*
 * The layout every record page shares (SQ-574): client, appointment,
 * estimate, job, invoice, equipment and Tools & Gear profiles, team member,
 * Settings and Pricing Setup. Behaviour is Android's, screen by screen; this
 * file is only what a wider screen adds:
 *
 *   rule 3  the page is one column, at most RECORD_MAX_WIDTH wide, centred on
 *           a soft grey backdrop when the page area is wider
 *   rule 4  the record's header (status and Edit / Save) is drawn in the
 *           green bar over the column, and only the body scrolls
 *   rule 5  the job site map beside the record from MAP_BESIDE_MIN
 *   rule 6  buttons and fields keep their phone size and wrap
 *
 * Every width is the page area's (the space beside the menu), never the
 * window's: the page measures its own container. Android's dp are CSS px and
 * the colours are the app theme's (ui/theme/Color.kt); the tokens are on .rk
 * in RecordKit.css.
 */

export const RECORD_MAX_WIDTH = 750;

/** The map opens beside the record once the page area is this wide. */
export const MAP_BESIDE_MIN = 800;

// ---- The header in the green bar (rule 4) -----------------------------------------

interface BarSpan {left: number; width: number; lineLeft: boolean; lineRight: boolean}

const sameSpan = (a: BarSpan | undefined, b: BarSpan) =>
  !!a && a.left === b.left && a.width === b.width && a.lineLeft === b.lineLeft && a.lineRight === b.lineRight;

/**
 * Where the record's header sits in the green bar: over the column, from its
 * left edge to its right edge, measured against the bar. On a phone the
 * column starts at the window's edge, so the header starts after the Menu
 * button instead. Re-measured when the column or the page area changes size
 * (the menu pinning, the window), and when the map opens or closes (`layout`:
 * past the cap that moves the column without resizing it).
 */
const useBarSpan = (column: HTMLElement | null, layer: HTMLElement | null, layout: string) => {
  const [span, setSpan] = useState<BarSpan>();
  useLayoutEffect(() => {
    if (!column || !layer) return;
    const measure = () => {
      const bar = layer.getBoundingClientRect();
      const col = column.getBoundingClientRect();
      const menu = document.querySelector('.app-bar-menu-button')?.getBoundingClientRect();
      const left = Math.max(col.left, menu ? menu.right + 8 : col.left) - bar.left;
      const right = col.right - bar.left;
      const next: BarSpan = {
        left, width: Math.max(0, right - left),
        lineLeft: col.left - bar.left > 1 && (!menu || col.left > menu.right),
        lineRight: right < bar.width - 1,
      };
      setSpan(current => sameSpan(current, next) ? current : next);
    };
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; measure(); });
    };
    measure();
    const resizes = new ResizeObserver(schedule);
    resizes.observe(column);
    const main = column.closest('main');
    if (main) resizes.observe(main);
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      resizes.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [column, layer, layout]);
  return span;
};

/**
 * Android's StickyStatusHeader, drawn into the green bar over `column`:
 * "Status" over the value on the left, the record's button on the right, and
 * faint lines carrying the column's edges up through the bar. Outside the
 * app's frame (no bar) it is a green strip at the top of the column.
 */
const StatusHeader = ({status, onStatusTap, action, column, layout}: {
  status: string;
  onStatusTap?: () => void;
  action?: ReactNode;
  column: HTMLElement | null;
  layout: string;
}) => {
  const [layer, setLayer] = useState<HTMLElement | null>(null);
  const [framed, setFramed] = useState(true);
  useEffect(() => {
    const found = document.getElementById(BAR_LAYER_ID);
    setLayer(found);
    setFramed(!!found);
  }, []);
  const span = useBarSpan(column, layer, layout);
  const statusText = (
    <>
      <div className="rk-status-caption">Status</div>
      <div className="rk-status-value">{status}</div>
    </>
  );
  const header = (
    <>
      {onStatusTap
        ? <button type="button" className="rk-status rk-status--tap" onClick={onStatusTap}>{statusText}</button>
        : <div className="rk-status">{statusText}</div>}
      {action}
    </>
  );
  if (!framed) return column ? createPortal(<div className="rk-bar-header rk-bar-header--strip">{header}</div>, column) : null;
  if (!layer || !span) return null;
  return createPortal(
    <>
      {span.lineLeft && <div className="rk-bar-edge" style={{left: span.left}}/>}
      {span.lineRight && <div className="rk-bar-edge" style={{left: span.left + span.width}}/>}
      <div className="rk-bar-header" style={{left: span.left, width: span.width}}>{header}</div>
    </>,
    layer,
  );
};

/** The record's button in the green bar: Edit, or Save in edit mode. */
export const HeaderButton = ({label, onClick, disabled}: {label: string; onClick: () => void; disabled?: boolean}) => (
  <button type="button" className="rk-header-button" onClick={onClick} disabled={disabled}>{label}</button>
);

// ---- The page (rules 3, 4 and 5) ------------------------------------------------------

/** Where the record's map is shown right now. */
export interface RecordMapLayout {
  /** Open beside the record (from MAP_BESIDE_MIN). */
  beside: boolean;
  /** Full size in the record's place (under MAP_BESIDE_MIN, after MAP). */
  alone: boolean;
  /** Back to the record (EST): only does something while the map is alone. */
  showRecord: () => void;
}

const RecordMapLayoutContext = createContext<RecordMapLayout>({beside: false, alone: false, showRecord: () => undefined});

/** The record's map, as its page lays it out: the record's own lines ("tap MAP on the left") depend on it. */
export const useRecordMapLayout = () => useContext(RecordMapLayoutContext);

export interface RecordPageProps {
  /** Names the page for the toolbar context, and clears the last page's title and buttons from the bar. Never shown. */
  name: string;
  /** The record's status. A page without one (Settings, the equipment profile) leaves the bar to the Menu button. */
  status?: string;
  /** Tapping the status moves the record on, where Android's does (an appointment: New, then Completed). */
  onStatusTap?: () => void;
  /** The record's button beside the status: a HeaderButton. */
  action?: ReactNode;
  /**
   * The job site map (estimate, job, invoice). Given, the page splits as rule
   * 5 says. A function is handed where the map is shown, so it can bring the
   * record back (a work item tapped on the full-size map).
   */
  map?: ReactNode | ((layout: RecordMapLayout) => ReactNode);
  /** Shows no map and no MAP tab although there is one: the customer preview. */
  mapHidden?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A record page: the column, its header in the green bar, the body that
 * scrolls under it and, when the record has one, the map beside it.
 *
 * With a map: from MAP_BESIDE_MIN the map is open on the left, sharing the
 * width half and half until the record reaches RECORD_MAX_WIDTH and taking
 * the rest from there; EST on the line between them closes it and MAP on the
 * record's left edge opens it again. Narrower it is Android: MAP swaps the
 * record for the map and EST swaps back. The record stays mounted behind the
 * map, so an edit in progress survives a look at it.
 */
export const RecordPage = ({name, status, onStatusTap, action, map, mapHidden, className, children}: RecordPageProps) => {
  const toolbar = useToolbar();
  useEffect(() => {
    toolbar.initToolbar(name, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const [frameRef, width] = useContentWidth<HTMLDivElement>();
  const [column, setColumn] = useState<HTMLDivElement | null>(null);
  const [mapAlonePane, setMapAlonePane] = useState<HTMLDivElement | null>(null);
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  // Wide: the map is open beside the record until it is closed. Narrow: it replaces the record until EST.
  const [mapClosedWide, setMapClosedWide] = useState(false);
  const [mapOpenNarrow, setMapOpenNarrow] = useState(false);

  const hasMap = !!map && !mapHidden;
  const wide = width >= MAP_BESIDE_MIN;
  const beside = hasMap && wide && !mapClosedWide;
  const alone = hasMap && !wide && mapOpenNarrow;

  // Rolling the wheel over the grey beside the column scrolls the record too.
  const scrollFromBackdrop = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) body?.scrollBy({top: e.deltaY, left: 0});
  };

  const layout = useMemo<RecordMapLayout>(() => ({beside, alone, showRecord: () => setMapOpenNarrow(false)}), [beside, alone]);
  const mapNode = typeof map === 'function' ? map(layout) : map;

  return (
    <RecordMapLayoutContext.Provider value={layout}>
    <div ref={frameRef} className={className ? `rk ${className}` : 'rk'}
         style={{'--rk-record-max': `${RECORD_MAX_WIDTH}px`} as React.CSSProperties}>
      {beside && (
        <div className="rk-map-side">
          {mapNode}
          <ViewSwitchTab label="EST" edge="end" onClick={() => setMapClosedWide(true)} ariaLabel="Close the map" style={{right: 0}}/>
        </div>
      )}
      {alone && (
        <div ref={setMapAlonePane} className="rk-map-alone">
          {mapNode}
          <ViewSwitchTab label="EST" edge="end" onClick={() => setMapOpenNarrow(false)} ariaLabel="Back to the record" style={{right: 0}}/>
        </div>
      )}
      <div className={beside ? 'rk-pane rk-pane--beside' : 'rk-pane'} hidden={alone} onWheel={scrollFromBackdrop}>
        <div ref={setColumn} className="rk-record">
          <div ref={setBody} className="rk-body">{children}</div>
          {hasMap && !beside && (
            <ViewSwitchTab label="MAP" edge="start" ariaLabel="Show the job site map" style={{left: 0}}
                           onClick={() => wide ? setMapClosedWide(false) : setMapOpenNarrow(true)}/>
          )}
        </div>
      </div>
      {status !== undefined && (
        <StatusHeader status={status} onStatusTap={onStatusTap} action={action} column={alone ? mapAlonePane : column}
                      layout={beside ? 'beside' : alone ? 'alone' : 'record'}/>
      )}
    </div>
    </RecordMapLayoutContext.Provider>
  );
};

/**
 * The name of a page that has no status of its own (Automatic follow-ups),
 * at the top of the page with the page's own buttons (Save changes) beside
 * it. The green bar shows nothing for such a page, and there is no back arrow.
 */
export const PageTitle = ({title, children}: {title: string; children?: ReactNode}) => (
  <div className="rk-page-title">
    <h1 className="rk-page-title-text">{title}</h1>
    {children}
  </div>
);

// ---- Buttons and fields keep their phone size and wrap (rule 6) ---------------------------

/**
 * Choice buttons (a brand, a category, a type, an operation, an answer): two
 * across on a phone, as on Android; on a wider page each keeps `width` and
 * the rows fill from the left, the leftover space waiting at the row's end
 * until another fits. Never stretched, and the gaps never change.
 */
export const Choices = ({width = 170, className, children}: {width?: number; className?: string; children: ReactNode}) => (
  <div className={className ? `rk-choices ${className}` : 'rk-choices'} style={{'--rk-choice-width': `${width}px`} as React.CSSProperties}>
    {children}
  </div>
);

/** A long list of names (models, items): one column on a phone, wrapping into columns on a wide page. */
export const NameColumns = ({children}: {children: ReactNode}) => <div className="rk-names">{children}</div>;

/**
 * A button that is full width on a phone (Save, Continue, Delete): it stops
 * at about phone width and sits centred instead of stretching across the page.
 */
export const WideButtonRow = ({children}: {children: ReactNode}) => <div className="rk-wide-button">{children}</div>;

/**
 * Two buttons that belong together (Create Appointment / Start an Estimate):
 * stacked at every width, the first on top, as on Android and iOS (SQ-745),
 * each a WideButtonRow's width.
 */
export const ButtonPair = ({children}: {children: ReactNode}) => <div className="rk-button-pair">{children}</div>;

/**
 * Short fields of a plain form with no view mode to match (VIN and tracking
 * number): side by side when the card has room for two at phone width,
 * stacked in Android's order when it hasn't. Measured on the card.
 */
export const FieldPair = ({children}: {children: ReactNode}) => <div className="rk-field-pair">{children}</div>;

/**
 * An address in view mode, on one line ("42 Cedar Ln, Boise, ID 83702")
 * that, when the line is too short, breaks in one place only: the street,
 * then "City, ST zip", which is how Android always shows it. Each part wraps
 * inside itself only when it alone is wider than the line.
 */
export const AddressLine = ({street, rest}: {street?: string; rest?: string}) => (
  <>
    {street && <span className="rk-addr-part">{rest ? `${street},` : street}</span>}
    {street && rest && ' '}
    {rest && <span className="rk-addr-part">{rest}</span>}
  </>
);

/**
 * An address in edit mode: Street | City | State | Zip on one line when the
 * card is wide enough, and Android's two lines (the street, then city, state
 * and zip) when it isn't.
 */
export const AddressFields = ({street, city, state, zip}: {street: ReactNode; city: ReactNode; state: ReactNode; zip: ReactNode}) => (
  <div className="rk-addr-fields">
    <div className="rk-addr-street">{street}</div>
    <div className="rk-addr-rest">
      <div className="rk-addr-city">{city}</div>
      <div className="rk-addr-state">{state}</div>
      <div className="rk-addr-zip">{zip}</div>
    </div>
  </div>
);

// ---- Records open as full pages (rule 2) ----------------------------------------------------

/**
 * A list page's record route. The list is mounted at `<base>/*`; `<base>/12`
 * is record 12 and `<base>/new` a new one, so a record has its own URL and
 * the browser's Back leaves it. A list whose records are not one number
 * (team members: users/3, invites/7) opens a path and reads `splat`. `close` goes back to the list: one step back
 * when the record was opened from it, else to the list itself (a bookmark,
 * a link from another page). It runs once per record, however many of the
 * editor's callbacks ask.
 */
export function useRecordRoute(base: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const splat = (useParams()['*'] ?? '').replace(/\/+$/, '');
  const id: number | 'NEW' | undefined = splat.toLowerCase() === 'new' ? 'NEW'
    : /^\d+$/.test(splat) ? Number(splat) : undefined;

  const closing = useRef(false);
  useEffect(() => { closing.current = false; }, [location.key]);

  const fromList = !!(location.state as {fromList?: boolean} | null)?.fromList;

  const open = useCallback((record: number | 'NEW' | string) =>
    navigate(`${base}/${record === 'NEW' ? 'new' : record}`, {state: {fromList: true}}), [navigate, base]);
  const close = useCallback(() => {
    if (splat === '' || closing.current) return;
    closing.current = true;
    if (fromList) navigate(-1);
    else navigate(base, {replace: true});
  }, [navigate, base, splat, fromList]);
  return {id, splat, open, close};
}

/**
 * A list page with one of its records open over it. The list stays mounted
 * and laid out underneath (only hidden), so going back finds the same filter
 * chip, the same search text and the same scroll position.
 */
export const ListWithRecord = ({list, record}: {list: ReactNode; record?: ReactNode}) => (
  <div className="rk-over-list">
    <div className={record ? 'rk-list-under rk-list-under--covered' : 'rk-list-under'} aria-hidden={record ? true : undefined}>
      {list}
    </div>
    {record && <div className="rk-over-list-record">{record}</div>}
  </div>
);

// ---- The pieces Android's record screens are built from ----------------------------------

/** The green-outlined row of record actions at the top of the page (ActionBarRow). Buttons wrap when they don't fit. */
export const ActionBar = ({children}: {children: ReactNode}) => <div className="rk-action-bar">{children}</div>;

/** RedActionButton (Delete, Cancel). */
export const RedButton = ({label, onClick, disabled}: {label: string; onClick: () => void; disabled?: boolean}) => (
  <button type="button" className="rk-red-button" onClick={onClick} disabled={disabled}>{label}</button>
);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A zone-less timestamp is UTC (the API's convention), shown in local time. */
const parseStamp = (value?: string | null): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
  return isNaN(date.getTime()) ? undefined : date;
};

const stampDate = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

/** When a record was created and last updated (SQ-480). */
export interface RecordDates {
  createdAt?: string;
  updatedAt?: string;
}

/** "Client #12" with Android's RecordStamps on the right: Created, and Updated only when it is later. */
export const TitleRow = ({title, dates}: {title: string; dates?: RecordDates}) => {
  const created = parseStamp(dates?.createdAt);
  const updated = parseStamp(dates?.updatedAt);
  return (
    <div className="rk-title-row">
      <div className="rk-title">{title}</div>
      {created && (
        <div className="rk-stamps">
          <div>Created {stampDate(created)}</div>
          {updated && updated.getTime() > created.getTime() && <div>Updated {stampDate(updated)}</div>}
        </div>
      )}
    </div>
  );
};

/**
 * CompanyBrandingHeader: the company's logo and name, centred. With no logo (or one that will not
 * load) there is no stand-in: the logo's space goes and the name, a size up, sits a little lower.
 */
export const CompanyBranding = ({name, logoUrl}: {name: string; logoUrl?: string | null}) => {
  const [failedUrl, setFailedUrl] = useState<string>();
  const showLogo = !!logoUrl && failedUrl !== logoUrl;
  return (
    <div className={showLogo ? 'rk-branding' : 'rk-branding rk-branding--no-logo'}>
      {showLogo && <img className="rk-logo" src={logoUrl} alt="" onError={() => setFailedUrl(logoUrl)}/>}
      <div className="rk-company-name">{name}</div>
    </div>
  );
};

/** The thin green rule between the rows of a record's card. */
export const Divider = () => <div className="rk-divider"/>;

/** The compact green pill beside a row (Call, Go, Email): a link out of the app. */
export const ActionPill = ({href, icon, label, external}: {href: string; icon: ReactNode; label: string; external?: boolean}) => (
  <a className="rk-pill" href={href} {...(external ? {target: '_blank', rel: 'noreferrer'} : {})}>
    {icon}{label}
  </a>
);

/** The page's loading or error line. */
export const RecordMessage = ({text, error}: {text: string; error?: boolean}) => (
  <div className={error ? 'rk-message rk-message--error' : 'rk-message'}>{text}</div>
);
